import React from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import type { CarbonAvoidanceStreamRow } from '../../types/carbon-avoidance-report.type';
import {
  carbonMaterialLabel,
  formatEfVersionsUsedForDisplay,
  formatTco2e,
  formatTonnes,
} from '../../utils/carbonAvoidance.util';

interface CarbonAvoidanceByStreamTableProps {
  rows: CarbonAvoidanceStreamRow[];
  title?: string;
  compact?: boolean;
  fillHeight?: boolean;
}

const CarbonAvoidanceByStreamTable: React.FC<CarbonAvoidanceByStreamTableProps> = ({
  rows,
  title = 'Carbon avoidance by stream',
  compact = false,
  fillHeight = false,
}) => {
  const theme = useTheme();
  const paperSx = {
    p: compact ? 2 : 3,
    borderRadius: '16px',
    boxShadow: theme.shadows[2],
    ...(fillHeight
      ? {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }
      : {}),
  };

  if (!rows.length) {
    return (
      <Paper sx={paperSx}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No carbon avoidance by stream for this period.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={paperSx}>
      <Typography variant={compact ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600, mb: 2 }}>
        {title}
      </Typography>
      <TableContainer sx={fillHeight ? { flex: 1 } : undefined}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Stream</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Avoided (tCO2e)
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>
                Weight (tonnes)
              </TableCell>
              <TableCell sx={{ fontWeight: 600 }}>EF versions used</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.material_label}>
                <TableCell>{carbonMaterialLabel(row.material_label)}</TableCell>
                <TableCell align="right">{formatTco2e(row.avoided_tco2e)}</TableCell>
                <TableCell align="right">{formatTonnes(row.weight_tonnes)}</TableCell>
                <TableCell>
                  {formatEfVersionsUsedForDisplay(row.emission_factor_versions_used)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default CarbonAvoidanceByStreamTable;
